import type {DropdownProps} from '@jetbrains/ring-ui/components/dropdown/dropdown'
import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import Popup from '@jetbrains/ring-ui/components/popup/popup'
import classNames from 'classnames'
import * as React from 'react'

import {getAgentHref, getBuildHref, getAgentTypeHref} from '../../../../../../routes'
import {getCanViewAgentDetails} from '../../../../../../selectors'
import type {AgentTypeId} from '../../../../../../types'
import Icon from '../../../../../common/Icon/Icon'
import Link from '../../../../../common/Link/Link'
import RouterLinkWrapper from '../../../../../common/RouterLinkWrapper/RouterLinkWrapper'
import type {
  AgentsTreeAgentNode,
  AgentsTreeCloudImageNode,
} from '../../../AgentsSidebar/AgentsSidebar.types'

import styles from './InstancesDropdown.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  instances: AgentsTreeCloudImageNode['children']
  agentTypeId: AgentTypeId
  anchor: DropdownProps['anchor']
}

const POPUP_DIRECTIONS = [
  Popup.PopupProps.Directions.BOTTOM_RIGHT,
  Popup.PopupProps.Directions.TOP_RIGHT,
]

function InstancesDropdownLink({
  href,
  title,
  children,
  className,
}: {
  children: React.ReactNode | string | undefined
  className?: string | undefined
  title?: string
  href?: string
}) {
  return href ? (
    <RouterLinkWrapper>
      <Link
        className={classNames(styles.link, className)}
        innerClassName={styles.linkInner}
        href={href}
        title={title}
      >
        {children}
      </Link>
    </RouterLinkWrapper>
  ) : (
    <span className={classNames(styles.link, styles.linkInner, styles.inactiveLink, className)}>
      {children}
    </span>
  )
}

function InstancesDropdownItem(instance: AgentsTreeAgentNode) {
  return (
    <div className={styles.item}>
      <InstancesDropdownLink
        title={instance.name}
        {...(useAppSelector(getCanViewAgentDetails) ? {href: getAgentHref(instance.id)} : {})}
      >
        {instance.title}
      </InstancesDropdownLink>
      {!instance.connected ? (
        <div className={styles.disconnectedStatus}>{'Disconnected'}</div>
      ) : !instance.enabled ? (
        <div className={styles.disabledStatus}>{'Maintenance'}</div>
      ) : instance.busy ? (
        <div className={styles.runningStatus}>
          <Icon className={styles.icon} name="running_green" />
          {instance.buildId != null ? (
            <InstancesDropdownLink href={getBuildHref(instance.buildId)}>
              {'Running'}
            </InstancesDropdownLink>
          ) : (
            'Running'
          )}
        </div>
      ) : (
        <div className={styles.idleStatus}>{'Idle'}</div>
      )}
    </div>
  )
}

const VISIBLE_INSTANCES_COUNT = 5

function InstancesDropdown({instances, agentTypeId, ...dropdownProps}: Props) {
  const canViewAgentDetails = useAppSelector(getCanViewAgentDetails)
  return (
    <Dropdown {...dropdownProps} className={styles.instancesDropdown}>
      <Popup directions={POPUP_DIRECTIONS} className={styles.popup}>
        {instances.slice(0, VISIBLE_INSTANCES_COUNT).map(instance => (
          <InstancesDropdownItem key={instance.id} {...instance} />
        ))}
        {instances.length > VISIBLE_INSTANCES_COUNT ? (
          canViewAgentDetails ? (
            <InstancesDropdownLink
              href={getAgentTypeHref(agentTypeId)}
              className={styles.viewAllLink}
            >{`View all ${instances.length} instances`}</InstancesDropdownLink>
          ) : (
            <InstancesDropdownLink>{`${instances.length} instances in total`}</InstancesDropdownLink>
          )
        ) : null}
      </Popup>
    </Dropdown>
  )
}

export default InstancesDropdown
